import { motion } from "framer-motion";
import { MessageSquare, Sparkles, CheckCircle2 } from "lucide-react";
import GlowEffect from "../common/GlowEffect";
import { useAIAgentData } from "../../hooks/useAIAgentData";

// Shared animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12 },
  }),
} as const;

export default function ComparisonSection() {
  const { data } = useAIAgentData();
  const { badgeText, title, description, headers, rows } = data.comparisonSection;

  return (
    <section className="relative px-5 sm:px-10 md:px-16 sm:py-16 py-8">
      <GlowEffect direction="left" />
      <div className="space-y-12">
        <div className="text-center space-y-4">
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-[#A6A0F6] text-sm font-medium tracking-widest uppercase">
            {badgeText}
          </motion.p>
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="text-4xl md:text-6xl font-bold text-white">
            {title}
          </motion.h2>
          <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} className="text-2xl text-[#d9d9d9]">
            {description}
          </motion.p>
        </div>

        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="rounded-2xl overflow-hidden border border-white/15">
          {/* Column headers */}
          <div className="grid grid-cols-2">
            <div className="sm:px-8 px-4 py-4 md:py-5 flex items-center justify-center gap-3 bg-white/[0.04] border-r border-white/10">
              <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
                <MessageSquare size={14} className="text-[#d9d9d9]" />
              </div>
              <span className="text-[#d9d9d9] font-semibold sm:text-lg text-base">{headers[0]}</span>
            </div>
            <div className="sm:px-8 px-4 py-4 md:py-5 flex items-center justify-center gap-3 bg-[#1179F9]/15">
              <div className="w-7 h-7 rounded-full bg-[#1179F9]/30 flex items-center justify-center">
                <Sparkles size={14} className="text-[#1179F9]" />
              </div>
              <span className="text-white font-semibold sm:text-lg text-base">{headers[1]}</span>
            </div>
          </div>

          {/* Rows */}
          {rows.map(([old, neu], i) => (
            <div key={i} className="grid grid-cols-2 border-t border-white/10">
              {/* Left — old way */}
              <div className="sm:px-8 px-4 py-4 md:py-5 flex items-center gap-3 border-r border-white/10 bg-white/[0.02]">
                <div className="w-5 h-5 rounded-full border border-white/20 flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-0.5 bg-white/40 rounded" />
                </div>
                <span className="text-[#c0c0c0] sm:text-lg text-base">{old}</span>
              </div>
              {/* Right — new way */}
              <div className="sm:px-8 px-4 py-4 md:py-5 flex items-center gap-3 bg-[#1179F9]/[0.06]">
                <CheckCircle2 size={18} className="text-[#1179F9] flex-shrink-0" />
                <span className="text-white font-medium sm:text-lg text-base">{neu}</span>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
